import type { ReactNode } from "react";
import { StyleProvider } from "@ant-design/cssinjs";
import { ConfigProvider, theme as antdTheme } from "antd";
import zhCN from "antd/locale/zh_CN";
import { useTheme } from "@/hooks/useTheme";

/**
 * GlassConfigProvider
 *
 * 将 Ant Design 的 ConfigProvider 与项目主题（亮/暗）结合：
 * - `StyleProvider layer` 让 antd 的 CSS-in-JS 样式包裹进 `@layer antd`，
 *   配合 src/styles/glass.css 的层序声明（antd < components < utilities），
 *   使 glass-* 组件类与 Tailwind 工具类可覆盖 antd 默认外观。
 * - 亮/暗 algorithm 随 useTheme 切换；token 调成半透明/玻璃友好，
 *   让 antd 的 Table/Form/Menu 等组件自然嵌进 glass-card 容器。
 */
export function GlassConfigProvider({ children }: { children: ReactNode }) {
  const { isDark } = useTheme();

  return (
    <StyleProvider layer>
      <ConfigProvider
        locale={zhCN}
        theme={{
          algorithm: isDark
            ? antdTheme.darkAlgorithm
            : antdTheme.defaultAlgorithm,
          token: {
            colorPrimary: "#1677ff",
            borderRadius: 10,
            colorBgContainer: isDark
              ? "rgba(30,32,44,0.55)"
              : "rgba(255,255,255,0.7)",
            colorBgElevated: isDark
              ? "rgba(30,32,44,0.78)"
              : "rgba(255,255,255,0.85)",
            colorBorderSecondary: isDark
              ? "rgba(255,255,255,0.12)"
              : "rgba(15,23,42,0.08)",
            colorBgLayout: "transparent",
          },
          components: {
            Menu: {
              itemBg: "transparent",
              subMenuItemBg: "transparent",
              itemSelectedBg: isDark
                ? "rgba(64,150,255,0.18)"
                : "rgba(22,119,255,0.12)",
              itemSelectedColor: isDark ? "#69b1ff" : "#1677ff",
              itemHoverBg: isDark
                ? "rgba(255,255,255,0.06)"
                : "rgba(15,23,42,0.04)",
            },
            Table: {
              headerBg: isDark
                ? "rgba(255,255,255,0.04)"
                : "rgba(15,23,42,0.03)",
              rowHoverBg: isDark
                ? "rgba(255,255,255,0.05)"
                : "rgba(15,23,42,0.03)",
              borderColor: isDark
                ? "rgba(255,255,255,0.1)"
                : "rgba(15,23,42,0.08)",
            },
            Card: {
              colorBgContainer: "transparent",
            },
            Input: {
              colorBgContainer: isDark
                ? "rgba(255,255,255,0.06)"
                : "rgba(255,255,255,0.55)",
              activeBorderColor: "#1677ff",
              hoverBorderColor: isDark ? "rgba(255,255,255,0.25)" : "#4096ff",
            },
            Modal: {
              contentBg: isDark
                ? "rgba(30,32,44,0.82)"
                : "rgba(255,255,255,0.82)",
              headerBg: "transparent",
            },
          },
        }}
      >
        {children}
      </ConfigProvider>
    </StyleProvider>
  );
}
